import React from "react";
import "../styles/shared-ui.css";
import "../styles/job-detail.css";
import SideTabs from "../components/layout/SideTabs";
import { useAppContext } from "../context/AppContext";
import { getCompanyNames } from "../services/companyService";
import {
  createProcess,
  getProcessesByCompanyId,
} from "../services/processService";
import { getProcessCategories } from "../services/processCategoryService";
import { getProcessStatuses } from "../services/processStatusService";
import {
  getTaskWithRelations,
  getTasksByCompanyId,
} from "../services/taskService";
import RichTextEditor from "../components/ui/RichTextEditor";
import { toast } from "../utils/toast";
import GatewaySection from "../components/ui/GatewaySection";
import WorkflowJobChips, {
  RaciLegendBar,
  mapJobTasksToWorkflowJobs,
} from "../components/ui/WorkflowJobChips";
import {
  exclusiveSkipsConvergence,
  gatewayRequiresConvergence,
} from "../utils/gatewayConvergence";

const START_EVENT_GATEWAY_ID = "__START_EVENT__";
const normalizeGatewayAfterTaskId = (value) =>
  String(value) === START_EVENT_GATEWAY_ID ? null
  : value ? Number(value)
  : null;

const ProcessCreatePage = () => {
  const { setActiveSection } = useAppContext();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [companies, setCompanies] = React.useState([]);
  const [processes, setProcesses] = React.useState([]);
  const [processCategories, setProcessCategories] = React.useState([]);
  const [processStatuses, setProcessStatuses] = React.useState([]);
  const [tasks, setTasks] = React.useState([]);
  const [workflowJobs, setWorkflowJobs] = React.useState([]); // per-row jobs for display
  const [gateways, setGateways] = React.useState([]); // { id, type, name, afterTaskId, branches }
  const [showChildProcessModal, setShowChildProcessModal] =
    React.useState(false);

  const [form, setForm] = React.useState({
    process_name: "",
    process_code: "",
    version: 0,
    company_id: "",
    process_status_id: "",
    process_category_id: "",
    parent_process_id: "",
    process_overview: "",
  });
  const [workflow, setWorkflow] = React.useState([]); // rows: { task_id, job_id }

  // Helper to load tasks for a specific company
  const loadTasksForCompany = async (companyId) => {
    if (!companyId) {
      setTasks([]);
      return;
    }
    try {
      const tasksRes = await getTasksByCompanyId(companyId).catch(() => []);
      setTasks(Array.isArray(tasksRes) ? tasksRes : []);
    } catch {
      setTasks([]);
    }
  };

  const loadProcessesForCompany = async (companyId) => {
    if (!companyId) {
      setProcesses([]);
      return;
    }
    try {
      const processesRes = await getProcessesByCompanyId(companyId).catch(
        () => [],
      );
      setProcesses(Array.isArray(processesRes) ? processesRes : []);
    } catch {
      setProcesses([]);
    }
  };

  const didInitRef = React.useRef(false);
  React.useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      try {
        setLoading(true);
        const companiesRes = await getCompanyNames().catch(() => []);
        setCompanies(Array.isArray(companiesRes) ? companiesRes : []);
        // Don't load tasks initially - wait for company selection
        setTasks([]);
        // Don't load parent processes initially - wait for company selection
        setProcesses([]);
        const [catsRes, statusRes] = await Promise.all([
          getProcessCategories().catch(() => []),
          getProcessStatuses().catch(() => []),
        ]);
        setProcessCategories(Array.isArray(catsRes) ? catsRes : []);
        setProcessStatuses(Array.isArray(statusRes) ? statusRes : []);
      } catch (e) {
        toast.error(e?.message || "Failed to load form data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onBack = () => setActiveSection("process-management");

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addWorkflowRow = () => {
    setWorkflow((prev) => [...prev, { task_id: "", job_id: "" }]);
    setWorkflowJobs((prev) => [...(Array.isArray(prev) ? prev : []), []]);
  };

  const removeWorkflowRow = (index) => {
    setWorkflow((prev) => prev.filter((_, i) => i !== index));
    setWorkflowJobs((prev) =>
      Array.isArray(prev) ? prev.filter((_, i) => i !== index) : [],
    );
  };

  // When a task is selected, auto-pick the first prelinked job (if any)
  const handleTaskSelect = async (index, taskId) => {
    // Update task_id immediately
    setWorkflow((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, task_id: taskId, job_id: "" } : row,
      ),
    );
    const idNum = Number(taskId);
    if (!Number.isFinite(idNum) || idNum <= 0) return;
    // Try to find jobs from cached tasks list first
    const cached = (tasks || []).find(
      (t) => Number(t.task_id || t.id) === idNum,
    );
    let jobsForTask = mapJobTasksToWorkflowJobs(cached?.jobTasks);
    let firstJobId = jobsForTask[0]?.job_id ?? null;
    if (!jobsForTask.length) {
      // Fetch relations on-demand if cache lacks jobTasks
      try {
        const rel = await getTaskWithRelations(idNum);
        jobsForTask = mapJobTasksToWorkflowJobs(rel?.jobTasks);
        firstJobId = jobsForTask[0]?.job_id ?? null;
      } catch (_) {
        // ignore errors; leave empty
      }
    }
    // Update visible jobs for this row
    setWorkflowJobs((prev) => {
      const copy = Array.isArray(prev) ? [...prev] : [];
      copy[index] = jobsForTask;
      return copy;
    });
    // Keep backend contract: store first job id if present
    if (firstJobId != null) {
      setWorkflow((prev) =>
        prev.map((row, i) =>
          i === index ? { ...row, job_id: String(firstJobId) } : row,
        ),
      );
    }
  };

  const moveWorkflowRowUp = (index) => {
    if (index <= 0) return;
    setWorkflow((prev) => {
      const copy = [...prev];
      const temp = copy[index - 1];
      copy[index - 1] = copy[index];
      copy[index] = temp;
      return copy;
    });
    setWorkflowJobs((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const tmp = arr[index - 1];
      arr[index - 1] = arr[index];
      arr[index] = tmp;
      return arr;
    });
  };

  const moveWorkflowRowDown = (index) => {
    setWorkflow((prev) => {
      if (index >= prev.length - 1) return prev;
      const copy = [...prev];
      const temp = copy[index + 1];
      copy[index + 1] = copy[index];
      copy[index] = temp;
      return copy;
    });
    setWorkflowJobs((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      if (index >= arr.length - 1) return arr;
      const tmp = arr[index + 1];
      arr[index + 1] = arr[index];
      arr[index] = tmp;
      return arr;
    });
  };

  // --- Gateway helpers (UI only for now) ---
  const workflowTaskOptions = React.useMemo(() => {
    return workflow
      .map((row, idx) => {
        const id = row.task_id;
        if (!id) return null;
        const t = tasks.find((x) => String(x.task_id || x.id) === String(id));
        const label =
          t ?
            `${t.task_name || t.name}${
              t.task_code || t.code ? ` [${t.task_code || t.code}]` : ""
            }`
          : `Task #${id}`;
        return { id: String(id), label, order: idx + 1 };
      })
      .filter(Boolean);
  }, [workflow, tasks]);

  const normalizeGatewayType = (type) =>
    String(type || "").trim().toUpperCase();

  const needsConvergence = (type) => {
    const t = normalizeGatewayType(type);
    return (
      t === "PARALLEL" || t === "INCLUSIVE" || t === "EXCLUSIVE" || t === "COMPLEX"
    );
  };

  // Converge is mandatory for merging types, except Exclusive with End + branch(es).
  const requiresConvergence = (gatewayOrType) => {
    if (gatewayOrType && typeof gatewayOrType === "object") {
      return gatewayRequiresConvergence(gatewayOrType, needsConvergence);
    }
    return needsConvergence(gatewayOrType);
  };

  const getConvergeAtTaskId = (gateway) => {
    const raw = gateway?.convergeAtTaskId ?? gateway?.converge_at_task_id ?? "";
    if (raw === "" || raw == null || Number(raw) === 0) return "";
    return String(raw);
  };

  const getConvergeGatewayName = (gateway) =>
    gateway?.convergeGatewayName ?? gateway?.converge_gateway_name ?? "";

  const getConvergeToEnd = (gateway) =>
    !!(gateway?.convergeToEnd ?? gateway?.converge_to_end);

  const hasConvergeTarget = (gateway) =>
    !!(
      getConvergeAtTaskId(gateway) ||
      getConvergeToEnd(gateway) ||
      gateway?.convergeAtGatewayId
    );

  const validate = () => {
    if (!form.process_name.trim()) return "Process Name is required";
    if (!form.process_code.trim()) return "Process Code is required";
    if (!form.company_id) return "Company is required";
    const overviewText = String(form.process_overview || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    if (overviewText.length < 10)
      return "Process Overview must be at least 10 characters";
    if (!Array.isArray(workflow) || workflow.length === 0)
      return "At least one workflow row is required";
    for (const [i, r] of workflow.entries()) {
      if (!r.task_id) return `Row ${i + 1}: Task is required`;
      if (!r.job_id) return `Row ${i + 1}: Job is required`;
    }
    for (const [i, g] of gateways.entries()) {
      const type = normalizeGatewayType(g.type);
      const isParallel = type === "PARALLEL";
      const totalBranches = Array.isArray(g.branches) ? g.branches.length : 0;
      const totalEndpoints =
        Array.isArray(g.endpoints) ? g.endpoints.length : 0;
      const totalOutgoing = totalBranches + totalEndpoints;

      if (!g.afterTaskId) return `Gateway ${i + 1}: After Task is required`;

      if (type === "EXCLUSIVE" && totalOutgoing < 2)
        return `Gateway ${i + 1}: Exclusive gateways require at least two outgoing paths`;

      if (isParallel) {
        if (totalBranches < 2)
          return `Gateway ${i + 1}: Parallel gateways require at least two branches`;
        if (totalEndpoints > 0)
          return `Gateway ${i + 1}: Parallel gateways cannot have end events`;
      }

      if (requiresConvergence(g)) {
        if (!hasConvergeTarget(g))
          return `Gateway ${i + 1}: Join point is required (a task, End event, or another gateway)`;
      }

      if (!Array.isArray(g.branches) || g.branches.length === 0)
        return `Gateway ${i + 1}: At least one branch is required`;

      const missingTargets = (g.branches || []).filter((b) => !b.targetTaskId);
      if (isParallel && missingTargets.length > 0) {
        return `Gateway ${i + 1}, Branch ${
          (g.branches || []).indexOf(missingTargets[0]) + 1
        }: Target task is required`;
      }
      if (!isParallel && type !== "EXCLUSIVE" && missingTargets.length > 0) {
        return `Gateway ${i + 1}, Branch ${
          (g.branches || []).indexOf(missingTargets[0]) + 1
        }: Target task is required`;
      }
      if (
        type === "EXCLUSIVE" &&
        totalEndpoints === 0 &&
        missingTargets.length > 0
      ) {
        return `Gateway ${i + 1}, Branch ${
          (g.branches || []).indexOf(missingTargets[0]) + 1
        }: Target task is required`;
      }

      // Two branches pointing at the same task/gateway is conflicting,
      // redundant routing — every outgoing path must go somewhere distinct.
      // A blank target (shared End event) is exempt.
      const targetCounts = new Map();
      (g.branches || []).forEach((b) => {
        if (!b.targetTaskId) return;
        const key = String(b.targetTaskId);
        targetCounts.set(key, (targetCounts.get(key) || 0) + 1);
      });
      const dupeIdx = (g.branches || []).findIndex(
        (b) => b.targetTaskId && targetCounts.get(String(b.targetTaskId)) > 1,
      );
      if (dupeIdx >= 0) {
        return `Gateway ${i + 1}, Branch ${
          dupeIdx + 1
        }: Another branch already routes to this same target`;
      }
    }
    return "";
  };

  const saveError = validate();
  const canSave = !saveError;

  const onSave = async () => {
    const v = validate();
    if (v) {
      toast.error(v);
      return;
    }
    try {
      setSaving(true);
      const payload = {
        process_name: form.process_name,
        process_code: form.process_code,
        version: form.version === "" ? 0 : Number(form.version),
        company_id: Number(form.company_id),
        process_status_id: form.process_status_id ? Number(form.process_status_id) : null,
        process_category_id: form.process_category_id ? Number(form.process_category_id) : null,
        parent_process_id:
          form.parent_process_id ? Number(form.parent_process_id) : null,
        process_overview: form.process_overview,
        workflow: workflow.map((r, idx) => {
          const item = { item_type: "task", order: idx + 1, task_id: Number(r.task_id) };
          if (r.job_id) item.job_id = Number(r.job_id);
          return item;
        }),
        gateways: gateways.map((g) => {
          const gwType = String(g.type || "").toUpperCase();
          const isParallel = gwType === "PARALLEL";
          const showConditions = ["EXCLUSIVE", "INCLUSIVE", "EVENT_BASED"].includes(gwType);

          // Merge regular branches and endpoints into single branches array
          const regularBranches = (g.branches || []).map((b) => {
            const rawTarget = b.targetTaskId;
            // Branch → gateway: "__GW__<localId>" → the target gateway's array
            // position (backend links it as target_gateway_id).
            const isGatewayTarget =
              typeof rawTarget === "string" && rawTarget.startsWith("__GW__");
            const branch = {
              targetTaskId: isGatewayTarget
                ? null
                : rawTarget
                  ? Number(rawTarget)
                  : null,
            };
            if (isGatewayTarget) {
              const ref = rawTarget.slice("__GW__".length);
              const gwIdx = gateways.findIndex(
                (x) => String(x.id) === String(ref),
              );
              if (gwIdx >= 0) branch.targetGatewayIndex = gwIdx;
            }
            if (showConditions) {
              branch.condition = b.condition || "";
            }
            return branch;
          });

          const endpointBranches =
            isParallel ?
              []
            : (g.endpoints || []).map((e) => {
                const endpoint = {
                  endTaskId: e.endTaskId ? Number(e.endTaskId) : null,
                  endEventName: e.endEventName || "",
                };
                if (showConditions) {
                  endpoint.condition = e.condition || "";
                }
                return endpoint;
              });

          const gateway = {
            type: (g.type || "").toUpperCase(),
            name: g.name || "",
            afterTaskId: normalizeGatewayAfterTaskId(g.afterTaskId),
            branches: [...regularBranches, ...endpointBranches],
          };

          const convergeAtTaskId = getConvergeAtTaskId(g);
          const convergeGatewayName = getConvergeGatewayName(g);
          const convergeToEnd = getConvergeToEnd(g);
          const skipConverge = exclusiveSkipsConvergence(g);

          // join → split: resolve target gateway local id → array index
          const gwTargetIdx =
            !skipConverge &&
            needsConvergence(g.type) &&
            g.convergeAtGatewayId
              ? gateways.findIndex(
                  (x) => String(x.id) === String(g.convergeAtGatewayId),
                )
              : -1;
          gateway.convergeAtGatewayIndex =
            gwTargetIdx >= 0 ? gwTargetIdx : undefined;
          const hasGwTarget = gateway.convergeAtGatewayIndex != null;

          gateway.convergeToEnd =
            !skipConverge &&
            !hasGwTarget &&
            needsConvergence(g.type) &&
            convergeToEnd;
          const convergeNum = Number(convergeAtTaskId);
          gateway.convergeAtTaskId =
            !skipConverge &&
            !hasGwTarget &&
            needsConvergence(g.type) &&
            !gateway.convergeToEnd &&
            convergeAtTaskId &&
            Number.isFinite(convergeNum) &&
            convergeNum > 0
              ? convergeNum
              : null;
          gateway.convergeGatewayName =
            !skipConverge &&
            needsConvergence(g.type) &&
            String(convergeGatewayName).trim()
              ? String(convergeGatewayName).trim()
              : null;

          return gateway;
        }),
      };
      await createProcess(payload);
      toast.success(
        `Process "${form.process_name}" has been created successfully!`,
      );
      try {
        localStorage.setItem("processesNeedsRefresh", "1");
      } catch {}
      setActiveSection("process-management");
    } catch (e) {
      const message = e?.message || "Failed to create process";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Create Process</h2>
            <p className="page-subtitle">
              Define basic details and workflow, then save.
            </p>
          </div>
          <div className="roles-toolbar">
            <button
              className="secondary-btn"
              onClick={onBack}
              disabled={saving}
            >
              Back
            </button>
            <button
              className="primary-btn"
              onClick={onSave}
              disabled={saving || !canSave}
              title={!canSave ? saveError : undefined}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ?
          <div className="no-results">Loading...</div>
        : <SideTabs
            defaultActiveId="basic"
            tabs={[
              {
                id: "basic",
                label: "Basic Details",
                content: (
                  <div className="role-card">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Process Name *</label>
                        <input
                          value={form.process_name}
                          onChange={(e) =>
                            handleChange("process_name", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Process Code *</label>
                        <input
                          value={form.process_code}
                          onChange={(e) =>
                            handleChange("process_code", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Version</label>
                        <input
                          value={form.version ?? ""}
                          disabled
                          style={{
                            backgroundColor: "#f9fafb",
                            cursor: "not-allowed",
                            color: "#6b7280",
                          }}
                          placeholder="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>Company *</label>
                        <select
                          value={form.company_id}
                          onChange={(e) => {
                            const val = e.target.value;
                            handleChange("company_id", val);
                            loadTasksForCompany(val);
                            loadProcessesForCompany(val);
                            // Reset workflow and parent process when company changes
                            setWorkflow([]);
                            setWorkflowJobs([]);
                            handleChange("parent_process_id", "");
                          }}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "14px",
                            color: "#111827",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">Select company</option>
                          {companies.map((c) => (
                            <option
                              key={c.company_id || c.id}
                              value={c.company_id || c.id}
                            >
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Process Status</label>
                        <select
                          value={form.process_status_id}
                          onChange={(e) =>
                            handleChange("process_status_id", e.target.value)
                          }
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "14px",
                            color: "#111827",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">Select status</option>
                          {processStatuses.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.description || `Status ${s.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Process Category</label>
                        <select
                          value={form.process_category_id}
                          onChange={(e) =>
                            handleChange("process_category_id", e.target.value)
                          }
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "14px",
                            color: "#111827",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">Select process category</option>
                          {processCategories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.description || c.name || `Category ${c.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Parent Process</label>
                        <select
                          value={form.parent_process_id}
                          onChange={(e) =>
                            handleChange("parent_process_id", e.target.value)
                          }
                          disabled={!form.company_id}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "14px",
                            color: !form.company_id ? "#9ca3af" : "#111827",
                            backgroundColor:
                              !form.company_id ? "#f9fafb" : "#fff",
                            cursor:
                              !form.company_id ? "not-allowed" : "pointer",
                          }}
                        >
                          <option value="">
                            {form.company_id ?
                              "Select parent process (optional)"
                            : "Select company first"}
                          </option>
                          {processes
                            .filter((p) => {
                              const processCompanyId =
                                p.company_id || p.company?.company_id;
                              return (
                                processCompanyId &&
                                String(processCompanyId) ===
                                  String(form.company_id)
                              );
                            })
                            .map((p) => (
                              <option
                                key={p.process_id || p.id}
                                value={p.process_id || p.id}
                              >
                                {p.process_name || p.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: "description",
                label: "Description",
                content: (
                  <div className="role-card">
                    <div className="form-group full">
                      <RichTextEditor
                        label="Process Overview *"
                        value={form.process_overview}
                        onChange={(html) =>
                          handleChange("process_overview", html)
                        }
                        height={220}
                      />
                    </div>
                  </div>
                ),
              },
              {
                id: "workflow",
                label: "Workflow",
                content: (
                  <div className="role-card">
                    <div
                      className="section-title"
                      style={{ marginBottom: 12, fontWeight: 600 }}
                    >
                      Tasks in Workflow
                    </div>
                    <RaciLegendBar />
                    <div style={{ overflowX: "auto" }}>
                      <div className="roles-table" style={{ width: "100%" }}>
                        <div
                          className="roles-table-header"
                          style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 60px auto" }}
                        >
                          <div className="cell">Task</div>
                          <div className="cell">Jobs &amp; RACI</div>
                          <div className="cell">Order</div>
                          <div className="cell" style={{ textAlign: "right" }}>
                            Actions
                          </div>
                        </div>
                        {workflow.length === 0 ?
                          <div className="no-results">
                            No workflow rows. Click "Add Row" to start.
                          </div>
                        : workflow.map((row, idx) => (
                            <div
                              key={idx}
                              className="roles-table-row"
                              style={{
                                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 60px auto",
                              }}
                            >
                              <div className="cell">
                                <select
                                  style={{
                                    width: "100%",
                                    padding: "8px 12px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    color: "#111827",
                                    backgroundColor: "#fff",
                                    cursor: "pointer",
                                  }}
                                  value={row.task_id}
                                  onChange={(e) =>
                                    handleTaskSelect(idx, e.target.value)
                                  }
                                >
                                  <option value="">Select task</option>
                                  {tasks.map((t) => (
                                    <option
                                      key={t.task_id || t.id}
                                      value={t.task_id || t.id}
                                    >
                                      {t.task_name || t.name}{" "}
                                      {t.task_code || t.code ?
                                        `- [${t.task_code || t.code}]`
                                      : ""}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="cell">
                                <WorkflowJobChips
                                  jobs={workflowJobs[idx]}
                                  taskId={row.task_id || null}
                                  showEditLink={!!row.task_id}
                                />
                              </div>
                              <div className="cell">
                                <span
                                  style={{
                                    display: "inline-block",
                                    minWidth: 28,
                                    textAlign: "center",
                                    padding: "4px 8px",
                                    background: "#f3f4f6",
                                    borderRadius: 6,
                                    border: "1px solid #e5e7eb",
                                  }}
                                >
                                  {idx + 1}
                                </span>
                              </div>
                              <div
                                className="cell"
                                style={{ textAlign: "right" }}
                              >
                                <div
                                  style={{
                                    display: "inline-flex",
                                    gap: 8,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <button
                                    className="secondary-btn sm"
                                    onClick={() => moveWorkflowRowUp(idx)}
                                    disabled={idx === 0}
                                  >
                                    ↑
                                  </button>
                                  <button
                                    className="secondary-btn sm"
                                    onClick={() => moveWorkflowRowDown(idx)}
                                    disabled={idx === workflow.length - 1}
                                  >
                                    ↓
                                  </button>
                                  <button
                                    className="danger-btn sm"
                                    onClick={() => removeWorkflowRow(idx)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                    <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                      <button
                        style={{
                          padding: "8px 16px",
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#1d4ed8";
                          e.target.style.boxShadow =
                            "0 2px 4px rgba(0,0,0,0.15)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#2563eb";
                          e.target.style.boxShadow =
                            "0 1px 3px rgba(0,0,0,0.12)";
                        }}
                        onClick={addWorkflowRow}
                      >
                        + Add Task
                      </button>
                      <button
                        style={{
                          padding: "8px 16px",
                          background: "#059669",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#047857";
                          e.target.style.boxShadow =
                            "0 2px 4px rgba(0,0,0,0.15)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#059669";
                          e.target.style.boxShadow =
                            "0 1px 3px rgba(0,0,0,0.12)";
                        }}
                        onClick={() => setShowChildProcessModal(true)}
                      >
                        + Add Child Process
                      </button>
                    </div>

                    {/* New Gateway Section Component */}
                    <GatewaySection
                      gateways={gateways}
                      onGatewaysChange={setGateways}
                      workflowTasks={workflowTaskOptions.map((opt, idx) => ({
                        task_id: opt.id,
                        task_name: opt.label,
                        order: opt.order || idx + 1,
                      }))}
                    />
                  </div>
                ),
              },
            ]}
          />
        }
      </div>

      {/* Add Child Process Modal */}
      {showChildProcessModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowChildProcessModal(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 500 }}
          >
            <div className="modal-header">
              <h3>Add Child Process</h3>
              <button
                className="modal-close"
                onClick={() => setShowChildProcessModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16, lineHeight: 1.6 }}>
                This process is new and hasn't been saved yet, so it cannot have
                child processes added to it at this time.
              </p>
              <p style={{ marginBottom: 0, lineHeight: 1.6 }}>
                To add child processes, first save this process as a parent
                process. Then, when creating other processes, you can select
                this process as their "Parent Process" in the Basic Details
                section.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="primary-btn"
                onClick={() => setShowChildProcessModal(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessCreatePage;
